import { useState, useEffect } from 'react'
import { HealthTrendChart } from '../components/dashboard/HealthTrendChart'
import { useNavigate } from 'react-router-dom'
import { 
  Briefcase, AlertTriangle, DollarSign, 
  Target, FolderKanban, ChevronRight,
  Brain, CheckCircle, Clock, BarChart
} from 'lucide-react'
import { dashboardService } from '../services/dashboard'
import { toast } from 'sonner'
import { getErrorMessage, formatCurrency } from '../utils/apiTransformers'
import { HeroSection } from '../components/HeroSection'
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts'
import type { ApiErrorLike } from '../utils/apiError'

interface PortfolioSummary {
  id: number
  name: string
  status: string
  project_count: number
  program_count?: number
  total_budget: number
  total_actual_cost: number
  budget_variance: number
  average_progress: number
  start_date: string | null
  end_date: string | null
}

export default function PortfolioDashboard() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [selectedTimeframe, setSelectedTimeframe] = useState<'month' | 'quarter' | 'year'>('month')
  const [portfolios, setPortfolios] = useState<PortfolioSummary[]>([])
  const [overview, setOverview] = useState<Awaited<ReturnType<typeof dashboardService.getExecutiveOverview>> | null>(null)
  const [insights, setInsights] = useState<Awaited<ReturnType<typeof dashboardService.getInsights>> | null>(null)
  const [risks, setRisks] = useState<Awaited<ReturnType<typeof dashboardService.getRiskIndicators>> | null>(null)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    setLoading(true)
    try {
      // getHealthMetrics also records today's health snapshot that feeds the trend chart.
      // AI insights load separately so they never hold up the page.
      dashboardService.getInsights().then(setInsights).catch(() => { /* panel stays empty */ })
      const [overviewData, portfolioSummary, , risksData] = await Promise.all([
        dashboardService.getExecutiveOverview(),
        dashboardService.getPortfolioSummary(),
        dashboardService.getHealthMetrics(),
        dashboardService.getRiskIndicators(),
      ])

      setOverview(overviewData)
      setPortfolios(portfolioSummary.portfolios)
      setRisks(risksData)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error fetching portfolio dashboard data:', error)
      toast.error(getErrorMessage(error) || 'Failed to load portfolio dashboard')
    } finally {
      setLoading(false)
    }
  }

  // Calculate portfolio metrics for HeroSection
  const portfolioMetrics = [
    { 
      label: 'Active Portfolios', 
      value: portfolios.filter(p => p.status === 'active').length.toString(), 
      sublabel: `+${portfolios.filter(p => p.status === 'active').length - (portfolios.length - portfolios.filter(p => p.status === 'active').length)}`, 
      icon: Briefcase,
      iconColor: 'bg-blue-50'
    },
    { 
      label: 'Total Investment', 
      value: formatCurrency(portfolios.reduce((sum, p) => sum + p.total_budget, 0)), 
      sublabel: '+12.3%', 
      icon: DollarSign,
      iconColor: 'bg-green-50'
    },
    { 
      label: 'Active Programs', 
      value: portfolios.length.toString(), 
      sublabel: `+${portfolios.length}`, 
      icon: FolderKanban,
      iconColor: 'bg-purple-50'
    },
    { 
      label: 'Active Projects', 
      value: portfolios.reduce((sum, p) => sum + p.project_count, 0).toString(), 
      sublabel: `-${0}`, 
      icon: Target,
      iconColor: 'bg-amber-50'
    },
  ]

  // Portfolio Status Distribution
  const portfolioStatusData = overview?.portfolio_status_breakdown ? [
    { name: 'On Track', value: overview.portfolio_status_breakdown.active || 0, color: '#10b981' },
    { name: 'At Risk', value: overview.portfolio_status_breakdown.on_hold || 0, color: '#f59e0b' },
    { name: 'Critical', value: overview.portfolio_status_breakdown.cancelled || 0, color: '#ef4444' },
  ] : []

  // Investment by Portfolio
  const investmentData = portfolios.map(p => ({
    portfolio: p.name.length > 15 ? p.name.substring(0, 15) + '...' : p.name,
    investment: p.total_budget / 1000000, // Convert to millions
    programs: p.program_count ?? 0,
    projects: p.project_count,
  }))


  // Top Performing Portfolios — Sprint 11 replaced the fake ROI
  // ((progress/100)*150) with real Budget Variance. We don't store
  // revenue anywhere, so ROI was unknowable; budget variance tells you
  // whether the portfolio is delivering within its planned cost — a
  // signal the executive view actually needs.
  const topPortfolios = portfolios
    .sort((a, b) => b.average_progress - a.average_progress)
    .slice(0, 3)
    .map(p => {
      const variancePct = p.total_budget > 0
        ? Math.round(((p.total_actual_cost - p.total_budget) / p.total_budget) * 100)
        : 0
      return {
        name: p.name,
        budgetVariancePct: variancePct,  // negative = under budget, positive = over
        health: Math.round(p.average_progress),
        programs: 0, // Would need program count
        projects: p.project_count,
        budget: p.total_budget,
        spent: p.total_actual_cost,
      }
    })

  // Critical Attention Items — Sprint 11 replaced `index + 5` mock
  // daysOpen with the real timestamp on the risk row when available.
  // If the risk service doesn't expose created_at (current API shape),
  // omit the field entirely rather than fabricating a number.
  const criticalItems = risks?.risks
    ?.filter((r) => r.severity === 'high' && r.project_name)
    .slice(0, 3)
    .map((risk) => {
      const createdAt = risk.created_at ? new Date(risk.created_at) : null
      const daysOpen = createdAt
        ? Math.max(0, Math.floor((Date.now() - createdAt.getTime()) / 86_400_000))
        : null
      return {
        portfolio: risk.project_name || 'Unknown Portfolio',
        issue: risk.message,
        severity: risk.severity,
        daysOpen,  // null when the risk record has no created_at
      }
    }) || []

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200'
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200'
      case 'low': return 'bg-green-100 text-green-700 border-green-200'
      default: return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  const totalBudget = portfolios.reduce((sum, p) => sum + p.total_budget, 0)
  const totalSpent = portfolios.reduce((sum, p) => sum + p.total_actual_cost, 0)
  const budgetUtilizationPercent = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading portfolio dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-8">
      <div className="max-w-[1600px] mx-auto space-y-6">
        {/* Hero Section */}
        <HeroSection
          title="Portfolio Dashboard"
          description="Real-time insights and performance metrics across all strategic portfolios"
          variant="level1"
          metrics={portfolioMetrics.map(m => ({
            label: m.label,
            value: m.value,
            sublabel: m.sublabel,
            icon: m.icon,
            iconColor: m.iconColor,
          }))}
          actionButton={{
            label: 'Create Portfolio',
            onClick: () => navigate('/portfolio/create'),
            icon: <Briefcase className="w-5 h-5" />,
          }}
        />

        {/* AI Insights Banner — Sprint 14: buttons now navigate. Fallback
            copy no longer invents a "projected 91% by Q1 2025" projection
            when no insight exists. */}
        {insights && (
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Brain className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-2">AI Portfolio Insights</h3>
                <p className="text-blue-100 mb-3">
                  {insights.working && insights.working.length > 0
                    ? (typeof insights.working[0] === 'string'
                        ? insights.working[0]
                        : insights.working[0]?.summary)
                    : 'No insights yet. Generate insights from a portfolio or project to see recommendations here.'}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => navigate('/ai')}
                    className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors text-sm"
                  >
                    View All Insights
                  </button>
                  <button
                    onClick={() => navigate('/governance')}
                    className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors text-sm"
                  >
                    Open Governance
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Key Metrics Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Portfolio Status Distribution */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Portfolio Status Distribution</h3>
            {portfolioStatusData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={portfolioStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {portfolioStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2">
                  {portfolioStatusData.map((item) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-gray-700">{item.name}</span>
                      </div>
                      <span className="font-medium text-gray-900">{item.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">No portfolio data available</div>
            )}
          </div>

          {/* Health over time (real daily snapshots) */}
          <HealthTrendChart title="Portfolio health over time" series={['portfolio_health', 'schedule_stability']} />

          {/* Budget Utilization */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Budget Utilization</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Total Allocated</span>
                  <span className="font-medium text-gray-900">{formatCurrency(totalBudget)}</span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full">
                  <div 
                    className="h-2 bg-blue-600 rounded-full" 
                    style={{ width: `${Math.min(100, budgetUtilizationPercent)}%` }} 
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Spent to Date</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(totalSpent)} ({Math.round(budgetUtilizationPercent)}%)
                  </span>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Remaining</span>
                  <span className="font-medium text-gray-900">{formatCurrency(totalBudget - totalSpent)}</span>
                </div>
              </div>
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-green-600 font-medium">
                    {budgetUtilizationPercent <= 100 ? 'On track with budget plan' : 'Over budget'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Investment by Portfolio Chart */}
        {investmentData.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Investment by Portfolio</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedTimeframe('month')}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                    selectedTimeframe === 'month'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  This Month
                </button>
                <button
                  onClick={() => setSelectedTimeframe('quarter')}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                    selectedTimeframe === 'quarter'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  This Quarter
                </button>
                <button
                  onClick={() => setSelectedTimeframe('year')}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                    selectedTimeframe === 'year'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  This Year
                </button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsBarChart data={investmentData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="portfolio" />
                <YAxis label={{ value: 'Investment ($M)', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="investment" fill="#3b82f6" name="Investment ($M)" />
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top Performing Portfolios & Critical Items */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Performing Portfolios */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Portfolios</h3>
            {topPortfolios.length > 0 ? (
              <div className="space-y-4">
                {topPortfolios.map((portfolio, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">{portfolio.name}</h4>
                        <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <FolderKanban className="w-4 h-4" />
                            {portfolio.programs} programs
                          </span>
                          <span className="flex items-center gap-1">
                            <Target className="w-4 h-4" />
                            {portfolio.projects} projects
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Budget Variance</div>
                        <div
                          className={`text-lg font-bold ${
                            portfolio.budgetVariancePct > 5
                              ? 'text-red-600'
                              : portfolio.budgetVariancePct < -5
                                ? 'text-green-600'
                                : 'text-gray-700'
                          }`}
                          title={portfolio.budgetVariancePct === 0
                            ? 'On budget'
                            : portfolio.budgetVariancePct > 0
                              ? 'Over budget'
                              : 'Under budget'}
                        >
                          {portfolio.budgetVariancePct > 0 ? '+' : ''}{portfolio.budgetVariancePct}%
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-gray-600 mb-1">Health Score</div>
                        <div className="w-32 h-2 bg-gray-200 rounded-full">
                          <div 
                            className="h-2 bg-green-600 rounded-full" 
                            style={{ width: `${portfolio.health}%` }} 
                          />
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Budget</div>
                        <div className="text-sm font-medium text-gray-900">
                          ${(portfolio.spent / 1000000).toFixed(1)}M / ${(portfolio.budget / 1000000).toFixed(1)}M
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">No portfolios available</div>
            )}
          </div>

          {/* Critical Attention Items */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Critical Attention Items</h3>
              <span className="px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                {criticalItems.length} Issues
              </span>
            </div>
            {criticalItems.length > 0 ? (
              <>
                <div className="space-y-3">
                  {criticalItems.map((item: { portfolio: string; issue: string; severity: string; daysOpen: number | null }, index: number) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg hover:border-red-300 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 mb-1">{item.portfolio}</div>
                          <div className="text-sm text-gray-600">{item.issue}</div>
                        </div>
                        <span className={`px-2.5 py-1 text-xs rounded border ${getSeverityColor(item.severity)}`}>
                          {item.severity}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        {item.daysOpen !== null && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {item.daysOpen} days open
                          </span>
                        )}
                        {/* Sprint 15 — risk row "View Details": jump to
                            the risks page, which is the only existing
                            surface that lists individual risks. */}
                        <button
                          onClick={() => navigate('/risks')}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          View Details
                          <ChevronRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => navigate('/risks')}
                  className="w-full mt-4 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  View All Issues
                </button>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">No critical issues</div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button 
              onClick={() => navigate('/portfolio')}
              className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left group"
            >
              <Briefcase className="w-6 h-6 text-gray-600 group-hover:text-blue-600 mb-2" />
              <div className="font-medium text-gray-900 text-sm">View All Portfolios</div>
            </button>
            <button className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left group">
              <BarChart className="w-6 h-6 text-gray-600 group-hover:text-blue-600 mb-2" />
              <div className="font-medium text-gray-900 text-sm">Portfolio Analytics</div>
            </button>
            <button className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left group">
              <AlertTriangle className="w-6 h-6 text-gray-600 group-hover:text-blue-600 mb-2" />
              <div className="font-medium text-gray-900 text-sm">Risk Management</div>
            </button>
            <button className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left group">
              <Target className="w-6 h-6 text-gray-600 group-hover:text-blue-600 mb-2" />
              <div className="font-medium text-gray-900 text-sm">Strategic Planning</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

